import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { Picker, type PickerItemData } from '@gitbook/react-uikit';
import { Button, ColorSwatch, Description, FieldGroup, Group, Label } from '@gitbook/spine-react';

import type { SiteSpaceCustomizationOverrides, SitesCustomizationPanelProps } from '../types';
import { setThemedColor } from '../utils';
import { ColorPickerForm } from './ColorPickerForm';
import { generateTintSuggestions } from './utils';

enum TintOptions {
    None = 'none',
    Inherit = 'inherit',
    Custom = 'custom',
}

const TINT_OPTION_PICKER: PickerItemData<TintOptions>[] = [
    {
        value: TintOptions.None,
        label: 'None',
    },
    /** Inherits from the header background color or primary color */
    {
        value: TintOptions.Inherit,
        label: 'Primary',
    },
    {
        value: TintOptions.Custom,
        label: 'Custom',
    },
];

/**
 * Tint settings for the site customization panel.
 */
export function TintSettings(
    props: SitesCustomizationPanelProps & { previewThemeMode: api.CustomizationThemeMode }
): React.ReactElement {
    const { settings, setSettings, previewThemeMode } = props;

    //Compute the base tint option from settings
    const settingsTintOption = React.useMemo(() => {
        if (!settings.styling?.tint) {
            return TintOptions.None;
        }
        if (getTintFallbackColor(settings, previewThemeMode)) {
            return TintOptions.Inherit;
        }
        return TintOptions.Custom;
    }, [settings, previewThemeMode]);

    const suggestedTintColorGroups = React.useMemo(
        () => generateTintSuggestions(settings.styling.primaryColor),
        [settings.styling.primaryColor]
    );

    // Only track if custom was explicitly selected
    const [explicitCustom, setExplicitCustom] = React.useState(false);

    // The active tint option is either custom (explicitly), or derived from settings
    const tintOption = explicitCustom ? TintOptions.Custom : settingsTintOption;

    return (
        <>
            <FieldGroup orientation="horizontal">
                <Group>
                    <Label>Tint color</Label>
                    <Description>
                        Add some color to the background and secondary elements. Try out some of the
                        suggested colors below.
                    </Description>
                </Group>
                <Picker
                    value={tintOption}
                    items={TINT_OPTION_PICKER}
                    onValueChange={(option) => {
                        setExplicitCustom(option === TintOptions.Custom);

                        switch (option) {
                            case TintOptions.None:
                                setSettings((prev) => ({
                                    ...prev,
                                    styling: {
                                        ...prev.styling,
                                        tint: undefined,
                                    },
                                }));
                                break;
                            case TintOptions.Inherit: {
                                setSettings((prev) => ({
                                    ...prev,
                                    styling: {
                                        ...prev.styling,
                                        tint: {
                                            color:
                                                settings.header?.backgroundColor ??
                                                settings.styling?.primaryColor,
                                        },
                                    },
                                }));
                                break;
                            }
                            case TintOptions.Custom: {
                                setSettings((prev) => ({
                                    ...prev,
                                    styling: {
                                        ...prev.styling,
                                        tint: {
                                            color:
                                                settings.styling?.tint?.color ??
                                                settings.header?.backgroundColor ??
                                                settings.styling?.primaryColor,
                                        },
                                    },
                                }));
                            }
                        }
                    }}
                />
            </FieldGroup>

            <ColorSuggestions
                previewThemeMode={previewThemeMode}
                groups={suggestedTintColorGroups}
                onColorSelect={(tintColor) => {
                    setSettings((prev) => ({
                        ...prev,
                        styling: {
                            ...prev.styling,
                            tint: {
                                color: tintColor,
                            },
                        },
                    }));
                }}
            />

            {tintOption === TintOptions.Custom && settings.styling.tint ? (
                <div className="bg-muted p-4">
                    <ColorPickerForm
                        {...props}
                        label="Custom tint"
                        description="Pick any color for your whole site, alongside your primary color"
                        currentColor={settings.styling.tint.color[previewThemeMode]}
                        setColor={(newColor) => {
                            setSettings((prev) => ({
                                ...prev,
                                styling: {
                                    ...prev.styling,
                                    tint: {
                                        color: setThemedColor(
                                            prev.styling?.tint?.color,
                                            previewThemeMode,
                                            newColor
                                        ),
                                    },
                                },
                            }));
                        }}
                        className="bg-[--color-background-main]"
                    />
                </div>
            ) : null}
        </>
    );
}

/** A list of custom tint color suggestions for the primary color */
function ColorSuggestions(props: {
    groups: Array<{
        title: string;
        colors: api.CustomizationThemedColor[];
    }>;
    onColorSelect: (tintColor: api.CustomizationThemedColor) => void;
    previewThemeMode: api.CustomizationThemeMode;
}) {
    return (
        <div className="flex flex-col gap-4">
            {props.groups.map((group) => (
                <ColorGroup
                    {...props}
                    key={group.title}
                    title={group.title}
                    colors={group.colors}
                    onSelect={props.onColorSelect}
                />
            ))}
        </div>
    );
}

/**
 * A group of color swatches with a title
 */
function ColorGroup(props: {
    title: string;
    colors: api.CustomizationThemedColor[];
    onSelect: (tintColor: api.CustomizationThemedColor) => void;
    previewThemeMode: api.CustomizationThemeMode;
}) {
    return (
        <div className="flex items-center justify-between gap-1">
            <h3 className="text-ui-heading-group">{props.title}</h3>
            <div className="flex items-center gap-2">
                {props.colors.map((color) => (
                    <Button
                        key={color[props.previewThemeMode]}
                        aria-label={`Apply ${props.title.toLowerCase()} tint color ${color[props.previewThemeMode]}`}
                        kind="pill"
                        size="small"
                        leadingIcon={<ColorSwatch color={color[props.previewThemeMode]} />}
                        label={color[props.previewThemeMode].toUpperCase()}
                        onClick={() => {
                            props.onSelect(color);
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

/**
 * Represents a color source that the tint color can sync with
 */
type TintFallbackColor = {
    type: 'backgroundColor' | 'primaryColor';
    source: api.CustomizationThemedColor;
};

/**
 * Parameters for updating a synced tint color when its source color changes
 */
type TintFallbackColorUpdate = {
    type: 'backgroundColor' | 'primaryColor';
    newColor: string;
};

/**
 * Manages tint color updates based on source color changes or initial creation.
 * The tint color can be synced with either the background color or primary color.
 * When synced, updating the source color will update the tint color automatically.
 */
export function updateTintColor(
    settings: SiteSpaceCustomizationOverrides,
    previewThemeMode: api.CustomizationThemeMode,
    update?: TintFallbackColorUpdate
): api.CustomizationTint | undefined {
    const colorSource = getTintFallbackColor(settings, previewThemeMode);
    if (!colorSource) {
        return settings.styling?.tint ?? undefined;
    }

    // Initial tint creation
    if (!update) {
        return { color: colorSource.source };
    }

    // Update tint if its source color is being changed
    const { type, source } = colorSource;
    if (type === update.type) {
        return {
            color: setThemedColor(source, previewThemeMode, update.newColor),
        };
    }

    return settings.styling?.tint ?? undefined;
}

/**
 * Determines if tint color matches a source color that should sync with in the UI.
 *
 * Priority: backgroundColor > primaryColor > explicit color
 * If tint color is not set, it will default to the backgroundColor or primaryColor if available.
 */
function getTintFallbackColor(
    settings: SiteSpaceCustomizationOverrides,
    previewThemeMode: api.CustomizationThemeMode
): TintFallbackColor | undefined {
    const { styling, header } = settings;
    const tintColor = styling?.tint?.color[previewThemeMode];

    if (tintColor) {
        if (tintColor === header?.backgroundColor?.[previewThemeMode]) {
            return { type: 'backgroundColor', source: header.backgroundColor };
        }
        if (tintColor === styling.primaryColor?.[previewThemeMode]) {
            return { type: 'primaryColor', source: styling.primaryColor };
        }

        // Tint is not synced with a source color
        return;
    }

    if (header?.backgroundColor) {
        return { type: 'backgroundColor', source: header.backgroundColor };
    }

    if (styling?.primaryColor) {
        return { type: 'primaryColor', source: styling.primaryColor };
    }
}
